import React, { useEffect, useState } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";

import PdfPreviewModal from "../../UIComponents/PdfPreviewModal";
import StyledScrollbar from "../../UIComponents/StyledScrollbar";
import useTemplateAsyncAction from "../hooks/useTemplateAsyncAction";
import { getOppositeTemplateSide, useTemplateContext } from "../TemplateContext";
import CreateTemplate from "./Common/CreateTemplate";
import TemplateSectionLoading from "./Common/TemplateSectionLoading";
import ImportTemplatePanel from "./Templates/ImportTemplatePanel";
import TemplateCard from "./Templates/TemplateCard";

import { getFileFromS3WithPresigned } from "../../../../utils/helpers";
import { canAccessTemplates } from "../../../../utils/Permissions";

import { getTemplates } from "../../../../actions/productTemplate";

import { ReactComponent as ImportTemplateIcon } from "../../../../assets/images/environment/ImportTemplateIcon.svg";
import { ReactComponent as RectangleLine } from "../../../../assets/images/sample-submission/RectangleLine.svg";

import "./styles/templates.css";
import "./Templates/ImportTemplatePanel.css";

export default function Templates(props) {
  const {
    indexOfTemplateSelected,
    templateList,
    isCreatingCoa,
    reportIsSelected,
    saveTemplateIdForAddingCoa,
    handleAddTemplateClick,
    handleEditTemplate,
    handleDeleteTemplate,
    handleCustomizeReport,
    isReportAndTemplateSelected,
    templateLoading,
    selectLoadingKey,
    onTemplatesImported,
  } = props;
  const { withSide, side } = useTemplateContext();
  const canImportTemplates = canAccessTemplates(getOppositeTemplateSide(side));
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatePreview, setTemplatePreview] = useState(null);
  const { loadingKey: cardActionLoadingKey, run: runCardAction, abort: abortCardAction } = useTemplateAsyncAction();
  const visibleTemplates = isCreatingCoa
    ? templateList.filter((item) => item.name.toLowerCase().includes(templateSearch.trim().toLowerCase()))
    : templateList;

  const closePreviewTemplate = () => {
    if (templatePreview?.url) {
      URL.revokeObjectURL(templatePreview.url);
    }
    setTemplatePreview(null);
  };

  useEffect(() => () => abortCardAction(), [abortCardAction]);

  const handlePreviewTemplate = (item) => runCardAction(async (signal) => {
    closePreviewTemplate();
    let filePath = item.preview_pdf_url;
    if (!filePath && item.template_id) {
      const response = await getTemplates(withSide({ template_id: item.template_id }), signal);
      if (response?.cancel || signal?.aborted) {
        return undefined;
      }
      if (!response?.success) {
        toast.error("Could not fetch template");
        return undefined;
      }
      filePath = response.result?.[0]?.preview_pdf_url;
    }
    if (!filePath) {
      toast.error("Preview link not available");
      return undefined;
    }
    const fileBlobObj = await getFileFromS3WithPresigned([filePath], "private", signal);
    if (signal?.aborted) {
      return undefined;
    }
    if (!fileBlobObj) {
      toast.error("Failed to preview");
      return undefined;
    }
    const blob = fileBlobObj[filePath];
    const url = window.URL.createObjectURL(blob);
    if (url) {
      setTemplatePreview({
        url,
        key: item.template_id || filePath,
        fileName: filePath.split("/").pop(),
        title: item.name || "Template",
      });
    } else {
      toast.error("Failed to preview");
    }
    return url;
  }, { useAbort: true, preventConcurrent: false, key: item.template_id });

  const handleEditTemplateClick = (templateId) => runCardAction(async (signal) => {
    closePreviewTemplate();
    return handleEditTemplate(templateId, signal);
  }, { useAbort: true, preventConcurrent: false, key: templateId });

  const handleDeleteTemplateClick = (templateId) => runCardAction(async (signal) => {
    closePreviewTemplate();
    return handleDeleteTemplate(templateId, signal);
  }, { useAbort: true, preventConcurrent: false, key: templateId });

  return (
    <div className={isCreatingCoa ? "templatesContainer--COA" : "templatesContainer"}>
      <div className={isCreatingCoa ? "templatesContainerChild--COA" : "templatesContainerChild"}>
        <div className="templatesHeaderContainer">
          <div className="templatesHeaderContainer__Title">
            <RectangleLine aria-hidden />
            <span>
              <span>
                {isCreatingCoa ? "Choose Template" : "Your Templates"}
              </span>
              {isCreatingCoa
                ? <p>Select from created templates.</p>
                : <p>Create custom Templates for your COA&apos;s </p>}
            </span>
          </div>
          {!isCreatingCoa && canImportTemplates && (
            <button
              type="button"
              className="ImportTemplateButton"
              onClick={() => setShowImportPanel(true)}
            >
              <ImportTemplateIcon className="ImportTemplateButton__Icon" aria-hidden />
              Import Template
            </button>
          )}
        </div>
        {isCreatingCoa && (
          <div className="TemplateChooseSearchWrap">
            <SearchOutlined className="TemplateChooseSearch__Icon" aria-hidden />
            <input
              type="text"
              className="TemplateChooseSearch"
              placeholder="Search templates..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
          </div>
        )}
        <StyledScrollbar
          className={isCreatingCoa ? "templatesScrollbar--COA" : "templatesScrollbar"}
          id="templatesScrollbar"
        >
          <div className={isCreatingCoa ? "templateBody templateBody--list" : "templateBody"}>
            {!isCreatingCoa && <CreateTemplate handleAddTemplateClick={handleAddTemplateClick} />}
            {!templateLoading && visibleTemplates.map((item, index) => (
              <TemplateCard
                key={item.template_id || index}
                index={templateList.indexOf(item)}
                item={item}
                isSelected={indexOfTemplateSelected === templateList.indexOf(item)}
                reportIsSelected={reportIsSelected}
                isCreatingCoa={isCreatingCoa}
                saveTemplateIdForAddingCoa={saveTemplateIdForAddingCoa}
                handleEditTemplate={handleEditTemplateClick}
                handleDeleteTemplate={handleDeleteTemplateClick}
                onPreviewTemplate={() => handlePreviewTemplate(item)}
                cardActionBusy={cardActionLoadingKey === item.template_id}
                selectBusy={selectLoadingKey === item.template_id}
              />
            ))}
            {templateLoading && <TemplateSectionLoading variant="inline" className="TemplateLoading" />}
          </div>
        </StyledScrollbar>
        {isCreatingCoa && (
        <Button
          type="primary"
          disabled={!isReportAndTemplateSelected || !!selectLoadingKey}
          className="Template__ReportBtn"
          onClick={handleCustomizeReport}
        >
          Customize Report
        </Button>
        )}
      </div>
      {templatePreview && (
        <PdfPreviewModal
          open
          title={templatePreview.title}
          onClose={closePreviewTemplate}
          files={[{
            key: templatePreview.key,
            fileName: templatePreview.fileName,
            src: templatePreview.url,
          }]}
          errorMessage="Unable to preview this template."
          width="40vw"
        />
      )}
      {showImportPanel && canImportTemplates && (
        <>
          <button
            type="button"
            className="ImportTemplatePanel__Backdrop"
            aria-label="Close import template panel"
            onClick={() => setShowImportPanel(false)}
          />
          <ImportTemplatePanel
            onClose={() => setShowImportPanel(false)}
            onImported={onTemplatesImported}
          />
        </>
      )}
    </div>
  );
}
